import {
  Component,
  Input,
  ViewChild,
  forwardRef,
  OnChanges,
  EventEmitter,
  Output,
  Inject,
  PLATFORM_ID,
  OnDestroy,
  ChangeDetectionStrategy
} from '@angular/core';
import {NG_VALUE_ACCESSOR, ControlValueAccessor} from '@angular/forms';
import {SizeSelectComponent} from '../size-select/size-select.component';
import {ChooseSizeDialogComponent} from '../choose-size-dialog/choose-size-dialog.component';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {Project} from '../../shared/models/project';
import {YesNoDialogComponent} from '../../yesno-dialog/yesno-dialog.component';
import {isPlatformBrowser} from '@angular/common';
import {WINDOW} from '../../shared/services/window.service';
import {SizeRange} from "../../shared/models/sizeRange";
import {Subscription} from "rxjs";

const noop = () => {
};

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => LazySizeSelectComponent),
  multi: true
};


@Component({
    selector: 'mbnm-lazy-size-select',
    templateUrl: './lazy-size-select.component.html',
    styleUrls: ['./lazy-size-select.component.scss'],
    providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
    host: {
        '(click)': 'loadAllOptions($event)',
        '[class.disabled]': 'disabled'
    },
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LazySizeSelectComponent implements OnChanges, ControlValueAccessor, OnDestroy {

  constructor(private dialog: MatDialog, @Inject(WINDOW) private window: Window, @Inject(PLATFORM_ID) private platformId: Object) { }

  loadedOptions: SizeRange;

  ngOnChanges(change) {
    if (change.range) {
      this.range = change.range.currentValue;
    }
  }

  @Input()
  readonly: boolean = false;

  @Input()
  label:string;

  @Input()
  range: SizeRange;

  @Input()
  placeholder:string;

  @Input()
  standardSizesLink:boolean = false;

  @Input()
  extraMessaging:string;

  @Input()
  extraOptions:Array<any>;

  @Input()
  multiCharFractions:boolean = false;

  @Input()
  project:Project;

  @Input()
  disabled:boolean = false;

  @Input()
  disabledMessaging:string;

  @Output()
  change:EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('sizeSelect') sizeSelect: SizeSelectComponent;

  public recentlyClosed: boolean = false;
  public loaded:boolean;
  public activated:boolean;
  private subscribedToOpen:boolean = false;
  private labelDiv:Element;
  private stdButtonDiv:Element;
  private sizeSelectOpenSubRef: Subscription;

  loadAllOptions($event) {
    const open = () => {
      if (this.sizeSelect) {
        if (!this.subscribedToOpen) {
          this.subscribedToOpen = true;
          this.sizeSelectOpenSubRef = this.sizeSelect.select.openedChange.subscribe((opened: boolean) => {
            if (opened) {
              setTimeout( () => {
                if (this.label) {
                  this.labelDiv = window.document.createElement('div');
                  this.labelDiv.className = 'select-label';
                  this.labelDiv.innerHTML = this.label;
                  this.sizeSelect.select.panel.nativeElement.parentNode.insertBefore(this.labelDiv, this.sizeSelect.select.panel.nativeElement);
                }

                if (this.standardSizesLink) {
                  this.stdButtonDiv = window.document.createElement('div');
                  this.stdButtonDiv.className = 'select-label std-btn';

                  let btn = window.document.createElement('button');
                  btn.className = 'mat-flat-button mat-accent';
                  btn.innerHTML = 'see standard sizes';
                  btn.addEventListener('click', () => this.showStandardSizes());
                  this.stdButtonDiv.appendChild(btn);

                  this.sizeSelect.select.panel.nativeElement.parentNode.appendChild(this.stdButtonDiv);
                }

                if (this.extraMessaging && this.sizeSelect.select.panel.nativeElement.querySelectorAll('.select-message').length === 0) {
                  let div = window.document.createElement('div');
                  div.className = 'select-message';
                  div.innerHTML = this.extraMessaging;
                  this.sizeSelect.select.panel.nativeElement.appendChild(div);
                }
              });
            } else {
              if (this.labelDiv) {
                this.labelDiv.remove();
              }
              if (this.stdButtonDiv) {
                this.stdButtonDiv.remove();
              }
              this.recentlyClosed = true;
              setTimeout( () => {
                this.recentlyClosed = false;
              }, 500);
            }
            this.sizeSelect.value = this.value;
          });
        }

        if (!this.recentlyClosed) {
          this.sizeSelect.select.open();
          this.activated = true;
        }
      }
    };

    $event.stopPropagation();
    if (!this.readonly) {
      if (!this.disabled) {
        if (isPlatformBrowser(this.platformId)) {
          this.loaded = true;
          setTimeout( () => {
            this.window.requestAnimationFrame(open);
          });
        }
      } else {
        let dialogRef = this.dialog.open(YesNoDialogComponent, {});
        dialogRef.componentInstance.data = {
          message: this.disabledMessaging,
          title: 'Why is this disabled?',
          closeMethod: 'button',
          yesButtonText: 'I Understand'
        }
      }
    }
  }


  //The internal data model
  private innerValue: any;

  //Placeholders for the callbacks which are later providesd
  //by the Control Value Accessor
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: any) => void = noop;

  //get accessor
  get value(): any {
    return this.innerValue;
  };

  //set accessor including call the onchange callback
  set value(v: any) {
    if (v !== this.innerValue && !isNaN(v)) {
      this.change.emit({
        oldValue: this.innerValue,
        newValue: v
      });
      this.innerValue = v;
      this.onChangeCallback(v);
    }
  }

  //Set touched on blur
  onBlur() {
    this.onTouchedCallback();
  }

  //From ControlValueAccessor interface
  writeValue(value: any) {
    if (value !== this.innerValue) {
      this.innerValue = value;

      if (this.innerValue !== null) {
        /*if (!this.loadedOptions) {
          if (this.options) {
            this.loadedOptions = this.options.filter( (opt) => {
              return opt.value == this.innerValue;
            });
          }
        } else {*/
          this.loadedOptions = this.range;
        //}
      }
    }
  }

  //From ControlValueAccessor interface
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  //From ControlValueAccessor interface
  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  public showStandardSizes(): void {

    const config = new MatDialogConfig();
    config.panelClass = 'fullscreen';

    const dialogRef = this.dialog.open(ChooseSizeDialogComponent, config);
    if (this.project.numOpenings > 0) {
      dialogRef.componentInstance.mode = 'BOTH-STANDARD';
      dialogRef.componentInstance.title = 'Choose a Standard Size';
    } else {
      dialogRef.componentInstance.mode = 'OUTER-STANDARD';
      dialogRef.componentInstance.title = 'Choose a Size';
    }

    dialogRef.afterClosed().subscribe((results:any) => {
      if (results) {
        this.sizeSelect.select.close();
        this.project.size.width = results.outer[0];
        this.project.size.height = results.outer[1];

        // clear opening if opening is 0x0
        if (results.opening && results.opening[0] === 0 && results.opening[1] === 0) {
          this.project.clearOpenings();
        }

        if (this.project.numOpenings === 1 && results.opening) {
          this.project.opening.size.width = results.opening[0];
          this.project.opening.size.height = results.opening[1];
        }
        this.project.bottomMargin = NaN;
      }
    });
  }

  ngOnDestroy() {
    if (this.sizeSelectOpenSubRef && !this.sizeSelectOpenSubRef.closed) {
      this.sizeSelectOpenSubRef.unsubscribe();
    }
  }
}
